import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useGetCountByCitiesCategoriesQuery, useGetFacilitiesQuery } from '../../../features/api/apiSlice';

const HotelFilters = ({ onFiltersChange, currentFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    name: currentFilters?.name || '',
    area: currentFilters?.area || '',
    priceRange: currentFilters?.priceRange || [0, 1000],
    rating: currentFilters?.rating || '',
    amenities: currentFilters?.amenities || [],
    roomType: currentFilters?.roomType || '',
    availability: currentFilters?.availability || 'all'
  });

  const { data: citiesData } = useGetCountByCitiesCategoriesQuery();
  const { data: facilitiesData } = useGetFacilitiesQuery();

  const areas = citiesData?.city_data?.map(city => city.City) || [];
  const amenities = facilitiesData?.data?.map(facility => ({
    value: facility.id.toString(), // Assuming facility ID is the value
    label: facility.name,
    icon: 'Check' // Default icon, replace with actual icons if available
  })) || [];

  const ratings = [
    { value: '', label: 'All Ratings' },
    { value: '5', label: '5 Stars' },
    { value: '4', label: '4+ Stars' },
    { value: '3', label: '3+ Stars' }
  ];

  const roomTypes = [
    { value: '', label: 'All Room Types' },
    { value: 'standard', label: 'Standard Room' },
    { value: 'deluxe', label: 'Deluxe Room' },
    { value: 'executive', label: 'Executive Suite' },
    { value: 'presidential', label: 'Presidential Suite' }
  ];

  useEffect(() => {
    setFilters({
      name: currentFilters?.name || '',
      area: currentFilters?.area || '',
      priceRange: currentFilters?.priceRange || [0, 1000],
      rating: currentFilters?.rating || '',
      amenities: currentFilters?.amenities || [],
      roomType: currentFilters?.roomType || '',
      availability: currentFilters?.availability || 'all'
    });
  }, [currentFilters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleAmenityToggle = (amenityValue) => {
    const currentAmenities = filters.amenities;
    const newAmenities = currentAmenities.includes(amenityValue)
      ? currentAmenities.filter(a => a !== amenityValue)
      : [...currentAmenities, amenityValue];

    handleFilterChange('amenities', newAmenities);
  };

  const clearFilters = () => {
    const clearedFilters = {
      name: '',
      area: '',
      priceRange: [0, 1000],
      rating: '',
      amenities: [],
      roomType: '',
      availability: 'all'
    };
    setFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const hasActiveFilters = () => {
    return filters.name ||
           filters.area ||
           filters.rating ||
           filters.roomType ||
           filters.amenities.length > 0 ||
           filters.availability !== 'all' ||
           filters.priceRange[0] > 0 ||
           filters.priceRange[1] < 1000;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      {/* Main Filter Row */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        {/* Hotel Name Search */}
        <div className="flex-1 min-w-[250px]">
          <label className="block text-sm font-medium text-foreground mb-2">
            Hotel Name
          </label>
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search hotels..."
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Area Filter */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-foreground mb-2">
            Area/Location
          </label>
          <select
            value={filters.area}
            onChange={(e) => handleFilterChange('area', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">All Areas</option>
            {areas.map((area) => (
              <option key={area} value={area}>{area}</option>
            ))}
          </select>
        </div>

        {/* Rating Filter */}
        <div className="flex-1 min-w-[150px]">
          <label className="block text-sm font-medium text-foreground mb-2">
            Star Rating
          </label>
          <select
            value={filters.rating}
            onChange={(e) => handleFilterChange('rating', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {ratings.map((rating) => (
              <option key={rating.value} value={rating.value}>{rating.label}</option>
            ))}
          </select>
        </div>

        {/* Price Range */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-foreground mb-2">
            Price Range (USD)
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="number"
              placeholder="Min"
              value={filters.priceRange[0]}
              onChange={(e) => handleFilterChange('priceRange', [parseInt(e.target.value) || 0, filters.priceRange[1]])}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <span className="text-muted-foreground">-</span>
            <input
              type="number"
              placeholder="Max"
              value={filters.priceRange[1]}
              onChange={(e) => handleFilterChange('priceRange', [filters.priceRange[0], parseInt(e.target.value) || 1000])}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-end space-x-2">
          {hasActiveFilters() && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="whitespace-nowrap"
            >
              <Icon name="X" size={16} className="mr-2" />
              Clear
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="whitespace-nowrap"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} className="mr-2" />
            {isExpanded ? 'Less' : 'More'} Filters
          </Button>
        </div>
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="border-t border-border pt-4 space-y-4">
          {/* Room Type Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Room Type
            </label>
            <select
              value={filters.roomType}
              onChange={(e) => handleFilterChange('roomType', e.target.value)}
              className="w-full max-w-xs px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {roomTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Availability Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Availability
            </label>
            <div className="flex space-x-4">
              {[
                { value: 'all', label: 'All Hotels' },
                { value: 'available', label: 'Rooms Available' },
                { value: 'booked', label: 'Check Availability' }
              ].map((option) => (
                <label key={option.value} className="flex items-center">
                  <input
                    type="radio"
                    name="availability"
                    value={option.value}
                    checked={filters.availability === option.value}
                    onChange={(e) => handleFilterChange('availability', e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Amenities Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Amenities & Services
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {amenities.map((amenity) => (
                <label
                  key={amenity.value}
                  className={`flex items-center p-2 border rounded-md cursor-pointer transition-all ${
                    filters.amenities.includes(amenity.value)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={filters.amenities.includes(amenity.value)}
                    onChange={() => handleAmenityToggle(amenity.value)}
                    className="mr-2"
                  />
                  <Icon name={amenity.icon} size={16} className="mr-2 text-muted-foreground" />
                  <span className="text-sm">{amenity.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters() && (
        <div className="border-t border-border pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Active filters:</span>
            {filters.name && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                Name: {filters.name}
                <button
                  onClick={() => handleFilterChange('name', '')}
                  className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {filters.area && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                Area: {filters.area}
                <button
                  onClick={() => handleFilterChange('area', '')}
                  className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {filters.rating && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                Rating: {ratings.find(r => r.value === filters.rating)?.label}
                <button
                  onClick={() => handleFilterChange('rating', '')}
                  className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {filters.roomType && (
              <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                Room: {roomTypes.find(t => t.value === filters.roomType)?.label}
                <button
                  onClick={() => handleFilterChange('roomType', '')}
                  className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            )}
            {filters.amenities.map((amenity) => (
              <span key={amenity} className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                {amenities.find(a => a.value === amenity)?.label}
                <button
                  onClick={() => handleAmenityToggle(amenity)}
                  className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelFilters;
